// The first second of a duel (TECH_SPEC §4bis.28).
//
// Both Sims have already fast-forwarded silently to the handover frame, so the
// canvas underneath is the real track at `startFrame`: obstacles ahead, both
// runners on the line. This overlay holds that frame for a beat, names who you
// are answering, and counts you in.
//
// **It is NOT a §4.5 pause.** No `PauseEvent` is recorded, no countdown event
// is sent, and the server never learns a duel had a countdown — exactly like
// the §4bis.15 stop-frame tutorial, and for the same reason: anything the
// server can see here would have to be part of verification.
//
// Fairness lives in this overlay. The challenger starts cold at ~2× opening
// speed with no runway, so the obstacle they must clear is shown to them,
// motionless, before the clock starts — which is also why the duel form of the
// `impossible_reaction` check skips that one obstacle (§4bis.28).

import { t } from '../i18n';
import { el, enter } from '../ui/dom';

export interface DuelIntroOptions {
  /** The sender's name and where they fell — 「{name} 在 {d} 米倒下」. */
  senderName: string;
  sourceEndM: number;
  /** Total hold, wall-clock. Longer on an untaught device (`duel.teach`). */
  countdownMs: number;
  /** Show the gesture legend: this device has never met the tutorial. */
  showTeach: boolean;
  /** Beat tick, for the count SFX. `0` is the final 「跑」. */
  onTick?: (n: number) => void;
  /**
   * §4bis.31: this link was a 帮我 hand-back, so the banner asks the OPPOSITE
   * question.
   *
   * Without this the recipient of a life read 「{name} 在 {d} 米倒下」 over
   * 「先活过他倒下的地方」 — *beat the person who just trusted you* — which is
   * the challenge screen, on the one entry the whole verb exists to create.
   * The addressing is the verb: T1 measures whether the ADDRESSED person comes
   * back, and until this branch existed nothing on screen told them they had
   * been named.
   *
   * `toIsYou` is computed client-side in `main.ts` (a cold `/r/:code`
   * navigation carries no device header, so the server cannot answer it), and
   * it changes only the WORDS. The link stays open to anyone: a third person
   * who opens it gets the `openOther` phrasing and plays exactly the same
   * slice — the card is addressed, the link is not.
   */
  handoff?: {
    /** Who handed it over. */
    fromName: string;
    /** Where the life has got to, and where you pick it up. */
    frontierM: number;
    startM: number;
    /** Is this device the person it was addressed to? */
    toIsYou: boolean;
  };
}

export interface DuelIntroHandle {
  /** Resolves when the beat ends and the run may start. */
  done: Promise<void>;
  /** Tear down without counting in (a rerun that left mid-count). */
  dismiss(): void;
}

/**
 * Holds the frozen handover frame for `countdownMs`, then resolves. Input is
 * the caller's problem — `session.ts` keeps `InputController` disabled for the
 * duration, so a panic tap during the count cannot be recorded as a frame-S
 * jump the player never meant.
 */
export function showDuelIntro(root: HTMLElement, opts: DuelIntroOptions): DuelIntroHandle {
  const number = el('div', { class: 'countdown-number', text: '' });
  const h = opts.handoff;
  // §4bis.31: same overlay, same frozen handover frame, same countdown — one
  // different sentence. A hand-back is a duel in every mechanical respect, so
  // giving it a second screen would be a second thing to maintain; what it is
  // NOT is the same ask, and the banner is where that lives.
  const headline = h
    ? h.toIsYou
      ? t('handoff.open', { name: h.fromName })
      : t('handoff.openOther', { p: h.frontierM })
    : t('duel.banner', { name: opts.senderName, d: opts.sourceEndM });
  const subline = h
    ? t('handoff.openSub', { p: h.frontierM, s: h.startM })
    : t('duel.aim');
  const screen = el('div', { class: 'screen screen--duel' }, [
    el('div', { class: 'duel-banner' }, [
      el('div', { class: 'intro-line', text: headline }),
      el('div', { class: 'intro-sub', text: subline }),
    ]),
    number,
    opts.showTeach ? el('span', { class: 'pill', text: t('duel.teach') }) : null,
  ]);
  root.append(screen);
  enter(screen);

  let cancelled = false;
  // Three beats plus the 「跑」, spread across the requested hold.
  const beat = Math.max(120, Math.round(opts.countdownMs / 4));

  const done = (async (): Promise<void> => {
    for (let n = 3; n >= 1; n--) {
      if (cancelled) return;
      number.textContent = String(n);
      opts.onTick?.(n);
      await wait(beat);
    }
    if (cancelled) return;
    number.textContent = t('countdown.go');
    opts.onTick?.(0);
    await wait(beat);
    if (cancelled) return;
    screen.remove();
  })();

  return {
    done,
    dismiss(): void {
      cancelled = true;
      screen.remove();
    },
  };
}

function wait(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}
